import React from 'react';

import { Field } from '@reckon-web/field';
import { Textarea } from '@reckon-web/textarea';
import { field, useForm, useSubmit, validate } from '@reckon-web/forms';

import { Dialog } from '../components/common/dialog';
import { useJobSignOffDecline as _useJobSignOffDecline } from '../data/useJobSignOffDecline';

import { Job } from './use-query-job';
import { useDialog } from './use-dialog';

type JobSignOffDeclineDialogResult = { note: string };

const JobSignOffDeclineDialog: React.FC<{
  onClose: (res?: JobSignOffDeclineDialogResult) => void;
}> = ({ onClose }) => {
  const schema = field.object({
    note: field.multilineText({
      validate: validate((value) => {
        validate.required(value);
        return value;
      }),
    }),
  });

  const form = useForm(schema);

  const submit = useSubmit(form, ({ note }) => onClose({ note }));

  return (
    <Dialog
      title="Decline sign off request?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, decline"
      onSubmit={submit}
    >
      <Field
        label="Note"
        description="Add a note describing why you are declining this sign off request"
      >
        <Textarea {...form.fields.note.props} />
      </Field>
    </Dialog>
  );
};

export const useJobSignOffDecline = () => {
  const [_jobSignOffDecline] = _useJobSignOffDecline(); // TODO: handle loading and error states

  const [Dialog, openDialog] = useDialog();

  const jobSignOffDecline = React.useCallback(
    async (job: Job) => {
      const res = await openDialog<JobSignOffDeclineDialogResult>(
        JobSignOffDeclineDialog,
        { size: 'small' }
      );
      if (!res) return;

      return _jobSignOffDecline({
        variables: { cloudId: job.cloudId || '', note: res.note },
      });
    },
    [_jobSignOffDecline, openDialog]
  );

  return React.useMemo(
    () =>
      [Dialog, jobSignOffDecline] as [typeof Dialog, typeof jobSignOffDecline],
    [Dialog, jobSignOffDecline]
  );
};
